/**
 * Invite Friends Modal
 * Viral referral system for CamPassed events - supports link and email invites
 */

import React, { useState, useEffect } from 'react';
import {
  Users,
  Send,
  X,
  Plus,
  Mail,
  Link2,
  Trash2,
  Check,
  AlertCircle,
  Share2,
  Copy,
  Sparkles,
  ExternalLink,
  MessageCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Single Invite Row
const InviteRow = ({ invite, index, onChange, onRemove, canRemove }) => (
  <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
    <div className="w-8 h-8 rounded-full bg-white/[0.06] flex items-center justify-center text-xs font-medium text-white/40">
      {index + 1}
    </div>
    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
      <Input
        placeholder="Name (optional)"
        value={invite.name}
        onChange={(e) => onChange(index, 'name', e.target.value)}
        className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30"
        data-testid={`invite-name-${index}`}
      />
      <Input
        type="email"
        placeholder="Email address"
        value={invite.email}
        onChange={(e) => onChange(index, 'email', e.target.value)}
        className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30"
        data-testid={`invite-email-${index}`}
      />
    </div>
    {canRemove && (
      <button
        onClick={() => onRemove(index)}
        className="p-2 rounded-lg hover:bg-white/[0.06] text-white/30 hover:text-rose-400 transition-colors"
        data-testid={`remove-invite-${index}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    )}
  </div>
);

// Success Result Row
const ResultRow = ({ result, index }) => (
  <div className={`flex items-center gap-3 p-3 rounded-xl ${
    result.success ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-rose-500/10 border border-rose-500/20'
  }`}>
    {result.success ? (
      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
    ) : (
      <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
    )}
    <div className="flex-1 min-w-0">
      <p className="text-sm text-white truncate">
        {result.referral?.invitee_name || result.referral?.invitee_email || result.invitee?.name || result.invitee?.email || `Invite ${index + 1}`}
      </p>
      {result.success ? (
        <div className="flex items-center gap-2">
          <p className="text-xs text-emerald-400">
            {result.email_sent ? 'Email sent' : 'Invite created'}
          </p>
          {result.invite_link && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(result.invite_link);
                toast.success('Link copied!');
              }}
              className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1"
            >
              <Copy className="w-3 h-3" />
              Copy link
            </button>
          )}
        </div>
      ) : (
        <p className="text-xs text-rose-400">{result.error}</p>
      )}
    </div>
  </div>
);

// Tab Button
const TabButton = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all ${
      active 
        ? 'bg-violet-500/20 border border-violet-500/30 text-violet-300' 
        : 'bg-white/[0.02] border border-white/[0.06] text-white/50 hover:text-white/70 hover:bg-white/[0.04]'
    }`}
    data-testid={`tab-${label.toLowerCase().replace(' ', '-')}`}
  >
    <Icon className="w-4 h-4" />
    <span className="text-sm font-medium">{label}</span>
  </button>
);

export default function InviteFriendsModal({ 
  isOpen, 
  onClose, 
  event, 
  organizationId,
  organizationName,
  session,
  onSuccess 
}) {
  const [activeTab, setActiveTab] = useState('link'); // 'link' or 'email'
  const [invites, setInvites] = useState([{ name: '', email: '' }]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [copied, setCopied] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setInvites([{ name: '', email: '' }]);
      setResults(null);
      setGeneratedLink(null);
      setCopied(false);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  const handleInviteChange = (index, field, value) => {
    const updated = [...invites];
    updated[index][field] = value;
    setInvites(updated);
  };
  
  const addInvite = () => {
    if (invites.length < 10) {
      setInvites([...invites, { name: '', email: '' }]);
    }
  };
  
  const removeInvite = (index) => {
    if (invites.length > 1) {
      setInvites(invites.filter((_, i) => i !== index));
    }
  };
  
  // Generate a shareable referral link
  const generateShareLink = async () => {
    if (!session?.access_token) {
      toast.error('Please sign in to create invite links');
      return;
    }
    
    setGeneratingLink(true);
    
    try {
      const res = await fetch(`${API_URL}/api/referrals`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event_id: event.id,
          organization_id: organizationId,
          event_name: event.name || event.event_name,
          organization_name: organizationName,
          invitee_email: null, // Link-based invite, no specific email
          invitee_phone: '0000000000', // Placeholder to satisfy validation
          send_email: false
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setGeneratedLink(data.invite_link);
        toast.success('Invite link generated!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to generate link');
      }
    } catch (error) {
      console.error('Failed to generate link:', error);
      toast.error('Failed to generate invite link');
    } finally {
      setGeneratingLink(false);
    }
  };
  
  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };
  
  const shareLink = async (link) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `You're invited to ${event.name || event.event_name}`,
          text: `Join me at ${event.name || event.event_name}!`,
          url: link
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyLink(link);
        }
      }
    } else {
      copyLink(link);
    }
  };
  
  // Send email invites
  const handleSubmitEmails = async () => {
    if (!session?.access_token) {
      toast.error('Please sign in to send invites');
      return;
    }
    
    // Filter out empty invites
    const validInvites = invites.filter(inv => inv.email && inv.email.includes('@'));
    
    if (validInvites.length === 0) {
      toast.error('Please add at least one valid email');
      return;
    }
    
    setLoading(true);
    setResults(null);
    
    try {
      const res = await fetch(`${API_URL}/api/referrals/bulk`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event_id: event.id,
          organization_id: organizationId,
          event_name: event.name || event.event_name,
          organization_name: organizationName,
          invitees: validInvites,
          send_email: true
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setResults(data.results);
        
        const successCount = data.results.filter(r => r.success).length;
        if (successCount > 0) {
          toast.success(`${successCount} invite${successCount > 1 ? 's' : ''} sent!`);
          if (onSuccess) {
            onSuccess(data);
          }
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send invites');
      }
    } catch (error) {
      console.error('Failed to send invites:', error);
      toast.error('Failed to send invites');
    } finally {
      setLoading(false);
    }
  };
  
  const validEmailCount = invites.filter(inv => inv.email && inv.email.includes('@')).length;
  const eventLink = `${window.location.origin}/discover`;
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div 
        className="bg-[#0f0f17] rounded-2xl border border-white/[0.08] max-w-xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
        data-testid="invite-friends-modal"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/[0.06]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/20">
                <Users className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Invite Friends</h2>
                <p className="text-sm text-white/40 truncate max-w-[200px]">{event?.name || event?.event_name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/[0.06] text-white/40 hover:text-white transition-colors"
              data-testid="close-invite-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Tab Selection */}
        <div className="p-4 flex gap-2">
          <TabButton
            active={activeTab === 'link'}
            onClick={() => setActiveTab('link')}
            icon={Link2}
            label="Share Link"
          />
          <TabButton
            active={activeTab === 'email'}
            onClick={() => setActiveTab('email')}
            icon={Mail}
            label="Email Invite"
          />
        </div>
        
        {/* Content */}
        <div className="p-6 pt-2 overflow-y-auto max-h-[50vh]">
          {activeTab === 'link' ? (
            /* Link Sharing Tab */
            <div className="space-y-4">
              <p className="text-sm text-white/60">
                Generate a unique invite link to share with friends via text, social media, or anywhere else.
              </p>
              
              {generatedLink ? (
                <div className="space-y-3">
                  <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/20">
                    <p className="text-xs text-violet-400 mb-2">Your personal invite link</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm text-white font-mono bg-black/30 px-3 py-2 rounded-lg truncate">
                        {generatedLink}
                      </code>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => copyLink(generatedLink)}
                      className="bg-white/[0.06] hover:bg-white/[0.1] text-white"
                      data-testid="copy-link-btn"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-2 text-emerald-400" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Link
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => shareLink(generatedLink)}
                      className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600"
                      data-testid="share-link-btn"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </Button>
                  </div>
                  
                  <Button
                    variant="ghost"
                    onClick={generateShareLink}
                    disabled={generatingLink}
                    className="w-full text-white/50 hover:text-white/70"
                  >
                    Generate new link
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={generateShareLink}
                  disabled={generatingLink}
                  className="w-full h-12 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600"
                  data-testid="generate-link-btn"
                >
                  {generatingLink ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      Generating...
                    </span>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4 mr-2" />
                      Generate Invite Link
                    </>
                  )}
                </Button>
              )}
              
              {/* Quick share info */}
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                <p className="text-xs text-white/40">
                  When someone uses your link, they'll be added to the event guest list and you'll both be credited in the referral system.
                </p>
              </div>
            </div>
          ) : results ? (
            /* Results after email submission */
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-amber-400" />
                <span className="text-white font-medium">
                  {results.filter(r => r.success).length} of {results.length} invites sent
                </span>
              </div>
              
              <div className="space-y-2">
                {results.map((result, i) => (
                  <ResultRow key={i} result={result} index={i} />
                ))}
              </div>
              
              <div className="pt-4 flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setResults(null);
                    setInvites([{ name: '', email: '' }]);
                  }}
                  className="flex-1 border-white/10 text-white"
                  data-testid="invite-more-btn"
                >
                  Invite More
                </Button>
                <Button
                  onClick={onClose}
                  className="flex-1 bg-gradient-to-r from-violet-500 to-purple-500"
                  data-testid="done-btn"
                >
                  Done
                </Button>
              </div>
            </div>
          ) : (
            /* Email invite form */
            <div className="space-y-4">
              <p className="text-sm text-white/60">
                Send personalized email invitations directly to your friends.
              </p>
              
              {/* Invite rows */}
              <div className="space-y-2">
                {invites.map((invite, i) => (
                  <InviteRow
                    key={i}
                    invite={invite}
                    index={i}
                    onChange={handleInviteChange}
                    onRemove={removeInvite}
                    canRemove={invites.length > 1}
                  />
                ))}
              </div>
              
              {/* Add more button */}
              {invites.length < 10 && (
                <button
                  onClick={addInvite}
                  className="w-full p-3 rounded-xl border border-dashed border-white/[0.1] text-white/40 hover:text-white/60 hover:border-white/20 transition-colors flex items-center justify-center gap-2"
                  data-testid="add-invite-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add another friend ({invites.length}/10)
                </button>
              )}
              
              {/* Info notice */}
              <div className="p-3 rounded-xl bg-violet-500/10 border border-violet-500/20">
                <p className="text-xs text-violet-300">
                  Your friends will receive a beautifully designed invite email with a unique code. When they join, you'll both be credited in the referral system.
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer - only show for email tab when no results */}
        {activeTab === 'email' && !results && (
          <div className="p-6 border-t border-white/[0.06]">
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 border-white/10 text-white"
                data-testid="cancel-invite-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitEmails}
                disabled={loading || validEmailCount === 0}
                className="flex-1 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600"
                data-testid="send-invites-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send {validEmailCount} Invite{validEmailCount !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
